import { useState, useEffect } from 'react';
import { Observable } from 'rxjs';
import { Option } from '@app/react-common/components/dropdown/Dropdown';
import { getSignOfCurrency } from '@utils/localization-utils';
import { getShopCountryCode } from '@app/pages/checkout-app/utils';
import { initRuleDisplayNameMap } from '@app/pages/checkout-app/configs';
import { TYPE_OPTIONS, STATUS_OPTIONS } from '../components';
import { Shippings } from '@app/pages/checkout-app/types';
import { Address } from '@pdqdev/pdq-shared/dist/entities/Address';
import { Shop as ShopModel } from '@app/services/shops/shop.model';

interface Shop {
  metadata?: {
    currency?: string;
    [key: string]: any;
  };
  shopLocations?: Address[];
  address?: Address;
  platform?: string;
}

interface UseDeliveryMethodsStateReturn {
  shopCurrency: string;
  shopCurrencyCode: string;
  shopCountryCode: string;
  enableCategoryBasedShipping: boolean;
  selectedIds: Set<string>;
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  typeFilter: Option;
  setTypeFilter: React.Dispatch<React.SetStateAction<Option>>;
  statusFilter: Option;
  setStatusFilter: React.Dispatch<React.SetStateAction<Option>>;
  bulkEditModalOpen: boolean;
  setBulkEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSelectItem: (id: string, checked: boolean) => void;
  filterMethod: (method: Shippings) => boolean;
}

export const useDeliveryMethodsState = (shop$: Observable<Shop>): UseDeliveryMethodsStateReturn => {
  const [shopCurrency, setShopCurrency] = useState('');
  const [shopCurrencyCode, setShopCurrencyCode] = useState('');
  const [shopCountryCode, setShopCountryCode] = useState('');
  const [enableCategoryBasedShipping, setEnableCategoryBasedShipping] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<Option>(TYPE_OPTIONS[0]);
  const [statusFilter, setStatusFilter] = useState<Option>(STATUS_OPTIONS[0]);
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);

  useEffect(() => {
    const subscription = shop$.subscribe((shop) => {
      if (shop) {
        setShopCurrency(getSignOfCurrency(shop?.metadata?.currency));
        setShopCurrencyCode(shop?.metadata?.currency || '');
        setShopCountryCode(getShopCountryCode(shop as ShopModel));
        setEnableCategoryBasedShipping(!!shop?.metadata?.enableCategoryBasedShipping);
      }
    });
    initRuleDisplayNameMap();

    return () => {
      subscription.unsubscribe();
    };
  }, [shop$]);

  const handleSelectItem = (id: string, checked: boolean) => {
    const newSelectedIds = new Set(selectedIds);
    if (checked) {
      newSelectedIds.add(id);
    } else {
      newSelectedIds.delete(id);
    }
    setSelectedIds(newSelectedIds);
  };

  const filterMethod = (method: Shippings): boolean => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      !searchQuery ||
      method.name?.toLowerCase().includes(searchLower) ||
      method.internalName?.toLowerCase().includes(searchLower);

    const category = method.shippingMethod?.category?.toLowerCase() || '';
    const matchesType =
      typeFilter.value === 'all' ||
      (typeFilter.value === 'domestic' && category.includes('domestic')) ||
      (typeFilter.value === 'international' && category.includes('international'));

    const matchesStatus =
      statusFilter.value === 'all' ||
      (statusFilter.value === 'active' && method.enabled) ||
      (statusFilter.value === 'inactive' && !method.enabled);

    return matchesSearch && matchesType && matchesStatus;
  };

  return {
    shopCurrency,
    shopCurrencyCode,
    shopCountryCode,
    enableCategoryBasedShipping,
    selectedIds,
    setSelectedIds,
    searchQuery,
    setSearchQuery,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    bulkEditModalOpen,
    setBulkEditModalOpen,
    handleSelectItem,
    filterMethod,
  };
};
